import { type FC } from "react";
import { shallowEqual, useSelector } from "react-redux";

import { CardGridArea, Tag } from "widgets";

import { type User } from "../../graphql.js";
import { type INuFeatureState } from "../../redux/modules/nuFeature.js";
import { type IAppState } from "../../redux/reducer.js";
import { AttributesListWidget } from "../core/navigation-panel/AttributeWidget.js";
import GenericEntitySummaryView from "./GenericEntitySummaryView.js";
import { DatabaseCard } from "./cards/DatabaseCard.js";
import { GenomeCard } from "./cards/GenomeCard.js";
import { NuFeatureCard } from "./cards/NuFeatureCard.js";
import { NuFeatureOrganismPropertiesCard } from "./cards/OrganismPropertiesCard.js";
import { ReferencesCard } from "./cards/ReferencesCard.js";
import RelatedProjectsCard from "./cards/RelatedProjectsCard.js";
import { renderNuFeatureAttributesWidgets } from "@/app-utils/attributes-widgets.tsx";

interface INuFeatureSummaryViewProps {
  title?: string;
}

const NuFeatureSummaryView: FC<INuFeatureSummaryViewProps> = (props) => {
  const { nuFeature, isLoading } = useSelector<IAppState, INuFeatureState>((state) => state.nuFeature, shallowEqual);

  const currentUser = useSelector<IAppState, User>((state) => state.auth.session, shallowEqual);

  return (
    <GenericEntitySummaryView
      title={(props.title ? `${props.title}: ` : "") + (nuFeature ? nuFeature.name : "")}
      subtitle={nuFeature?.description || "Nucleotide Feature Summary"}
      entity={nuFeature}
      refGenomes={undefined}
      headerAttributes={[
        <AttributesListWidget
          attributes={renderNuFeatureAttributesWidgets(nuFeature)}
          key={"nuFeature-summary-view-widget-1"}
        />,
      ]}
      headerTags={[
        <Tag key={"nuFeature-summary-view-tag-1"} intentColor={nuFeature?.nuFeatureType?.color}>
          {nuFeature?.nuFeatureType.name}
        </Tag>,
      ]}
      isLoading={isLoading}
      getEntityName={() => nuFeature?.name}
    >
      {nuFeature && (
        <>
          <NuFeatureCard nuFeature={nuFeature} area={CardGridArea.Left} />
          <DatabaseCard
            genome={nuFeature.genome}
            database={nuFeature.region.contig.database}
            area={CardGridArea.Left}
          />
          <NuFeatureOrganismPropertiesCard feature={nuFeature} area={CardGridArea.Left} />
          <ReferencesCard references={nuFeature.externalReferences} area={CardGridArea.Right} />
          <GenomeCard genome={nuFeature.genome} area={CardGridArea.Right} />
          <RelatedProjectsCard
            relatedProjects={nuFeature.relatedProjects}
            currentUser={currentUser}
            area={CardGridArea.Panel}
          />
        </>
      )}
    </GenericEntitySummaryView>
  );
};

export default NuFeatureSummaryView;
